import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  LinearProgress,
  Tooltip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  MonetizationOn as MonetizationOnIcon,
  Comment as CommentIcon,
  AccountBalanceWallet as WalletIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  AccountBalance as AccountBalanceIcon,
  Security as SecurityIcon,
  Payments as PaymentsIcon,
  ReceiptLong as ReceiptLongIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon
} from '@mui/icons-material';
import financialRequestService from '../../../services/financialRequestService';
import walletService from '../../../services/walletService';

// Helper function to get status color
const getStatusColor = (status) => {
  switch (status) {
    case 'Requested':
      return 'info';
    case 'Review':
      return 'warning';
    case 'Approved':
      return 'success';
    case 'Declined':
      return 'error';
    case 'MemberRejected':
      return 'error';
    case 'Disbursed':
      return 'success';
    case 'Completed':
      return 'success';
    default:
      return 'default';
  }
};

// Format date helper
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// SLA configuration for each status (in hours)
const SLA_CONFIG = {
  'Requested': 24, // 24 hours to move from Requested to Review
  'Review': 48,    // 48 hours to move from Review to Approved/Declined
  'Approved': 72,  // 72 hours to disburse funds after approval
  'Disbursed': 72  // 72 hours for member to confirm receipt
};

// Helper function to calculate SLA status
const getSlaStatus = (status, startDate) => {
  if (!status || !startDate || !SLA_CONFIG[status]) return 'none';
  
  const now = new Date();
  const statusDate = new Date(startDate);
  const elapsedHours = (now - statusDate) / (1000 * 60 * 60);
  const slaLimit = SLA_CONFIG[status];
  
  // Calculate SLA threshold percentages
  const warningThreshold = 0.7; // 70% of SLA time used = amber
  const criticalThreshold = 1.0; // 100% of SLA time used = red
  
  if (elapsedHours >= slaLimit * criticalThreshold) {
    return 'red'; // Exceeded SLA
  } else if (elapsedHours >= slaLimit * warningThreshold) {
    return 'amber'; // Approaching SLA limit
  } else {
    return 'green'; // Within SLA
  }
};

// Helper function to get time remaining text for SLA
const getSlaTimeRemaining = (status, startDate) => {
  if (!status || !startDate || !SLA_CONFIG[status]) return '';
  
  const now = new Date();
  const statusDate = new Date(startDate);
  const elapsedHours = (now - statusDate) / (1000 * 60 * 60);
  const slaLimit = SLA_CONFIG[status];
  
  if (elapsedHours >= slaLimit) {
    const overBy = Math.floor(elapsedHours - slaLimit);
    return `Exceeded by ${overBy} hour${overBy !== 1 ? 's' : ''}`;
  } else {
    const remaining = Math.ceil(slaLimit - elapsedHours);
    return `${remaining} hour${remaining !== 1 ? 's' : ''} remaining`;
  }
};

// Format currency helper
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount);
};

const MemberFinancialRequestDetailPage = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState(null);
  const [error, setError] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  
  // State for comment
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // State for decision dialogs
  const [openAcceptDialog, setOpenAcceptDialog] = useState(false);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [openConfirmReceiptDialog, setOpenConfirmReceiptDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  // State for wallet information
  const [walletInfo, setWalletInfo] = useState(null);
  const [loadingWallet, setLoadingWallet] = useState(false);
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [walletTotals, setWalletTotals] = useState({
    totalReceived: 0,
    pendingTransactions: 0
  });

  // Steps for the stepper
  const steps = ['Requested', 'In Review', 'Approved', 'Disbursed', 'Completed'];

  // Map request status to stepper step
  const getStepFromStatus = (status) => {
    switch (status) {
      case 'Requested':
        return 0;
      case 'Review':
        return 1;
      case 'Approved':
      case 'MemberRejected':
      case 'Declined':
        return 2;
      case 'Disbursed':
        return 3;
      case 'Completed':
        return 4;
      default:
        return 0;
    }
  };

  useEffect(() => {
    const fetchRequest = async () => {
      try {
        const response = await financialRequestService.getRequestById(requestId);
        setRequest(response.data);
        setActiveStep(getStepFromStatus(response.data.status));
      } catch (err) {
        console.error('Error fetching request details:', err);
        setError(err.message || 'Error loading the request details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (requestId) {
      fetchRequest();
    }
  }, [requestId]);

  useEffect(() => {
    // Fetch wallet info if request status is Disbursed or Completed
    const fetchWalletInfo = async () => {
      if (request && (request.status === 'Disbursed' || request.status === 'Completed')) {
        setLoadingWallet(true);
        try {
          // Fetch wallet data with error handling
          let walletData;
          let transactionsData;
          
          try {
            // Attempt to fetch real wallet data
            walletData = await walletService.getWallet();
            setWalletInfo(walletData.data);
            
            // Attempt to fetch real transaction data  
            transactionsData = await walletService.getTransactions();
            setWalletTransactions(transactionsData.data || []);
            
            // Calculate totals from real data
            let totalReceived = 0;
            let pendingTransactions = 0;
            
            if (transactionsData.data && Array.isArray(transactionsData.data)) {
              transactionsData.data.forEach(tx => {
                if (tx.type === 'credit' && tx.status === 'completed') {
                  totalReceived += tx.amount;
                }
                if (tx.status === 'pending') {
                  pendingTransactions += 1;
                }
              });
            }
            
            setWalletTotals({
              totalReceived,
              pendingTransactions
            });
          } catch (err) {
            console.error('Error fetching real wallet data:', err);
            
            // If wallet endpoint not implemented, throw error to be handled by outer catch
            throw new Error('Wallet service not available. Please contact support.');
          }
        } catch (err) {
          console.error('Error in wallet data processing:', err);
          
          // Show error in UI but don't prevent the page from loading
          // Instead of setting a general error, we'll add a specific wallet error
          setWalletInfo({
            error: true,
            message: err.message || 'Unable to load wallet information'
          });
        } finally {
          setLoadingWallet(false);
        }
      }
    };
    
    fetchWalletInfo();
  }, [request]);

  const handleAddComment = async () => {
    if (!comment.trim()) return;
    
    setIsSubmitting(true);
    try {
      const response = await financialRequestService.addComment(requestId, comment);
      setRequest(response.data);
      setComment('');
    } catch (err) {
      console.error('Error adding comment:', err);
      setError('Failed to add comment: ' + (err.message || 'Unknown error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAcceptRequest = async () => {
    setIsSubmitting(true);
    try {
      const response = await financialRequestService.updateRequestStatus(
        requestId, 
        'Approved', 
        'Member accepted the approval'
      );
      setRequest(response.data);
      setOpenAcceptDialog(false);
    } catch (err) {
      console.error('Error accepting request:', err);
      setError('Failed to accept request: ' + (err.message || 'Unknown error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRejectRequest = async () => {
    if (!rejectionReason.trim()) return;
    
    setIsSubmitting(true);
    try {
      const response = await financialRequestService.updateRequestStatus(
        requestId, 
        'MemberRejected', 
        `Member rejected: ${rejectionReason}`
      );
      setRequest(response.data);
      setRejectionReason('');
      setOpenRejectDialog(false);
    } catch (err) {
      console.error('Error rejecting request:', err);
      setError('Failed to reject request: ' + (err.message || 'Unknown error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmReceipt = async () => {
    setIsSubmitting(true);
    try {
      const response = await financialRequestService.updateRequestStatus(
        requestId, 
        'Completed', 
        'Member confirmed receipt of funds'
      );
      setRequest(response.data);
      setOpenConfirmReceiptDialog(false);
    } catch (err) {
      console.error('Error confirming receipt:', err);
      setError('Failed to confirm receipt: ' + (err.message || 'Unknown error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Status display mapping
  const statusDisplay = {
    'Requested': 'Requested',
    'Review': 'In Review',
    'Approved': 'Approved',
    'Declined': 'Declined',
    'MemberRejected': 'Rejected by You',
    'Disbursed': 'Funds Disbursed',
    'Completed': 'Completed'
  };

  // Enhanced wallet card component
  const renderEnhancedWallet = () => {
    if (!(request.status === 'Disbursed' || request.status === 'Completed')) {
      return null;
    }
    
    return (
      <Grid item xs={12}>
        <Card variant="outlined" 
          sx={{ 
            mb: 3, 
            overflow: 'hidden', 
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #eef2f7 100%)'
          }}
        >
          <Box sx={{ 
            p: 1.5, 
            bgcolor: 'primary.main', 
            color: 'primary.contrastText',
            display: 'flex',
            alignItems: 'center',
          }}>
            <WalletIcon sx={{ mr: 1 }} />
            <Typography variant="h6" component="h3">Enterprise Financial Wallet</Typography>
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
              <Tooltip title="Secured with enterprise-grade encryption">
                <SecurityIcon fontSize="small" sx={{ mx: 1 }} />
              </Tooltip>
              <Chip 
                size="small" 
                label={request.status === 'Disbursed' ? "FUNDS AVAILABLE" : "CONFIRMED"} 
                sx={{ 
                  bgcolor: request.status === 'Disbursed' ? 'warning.main' : 'success.main',
                  color: 'white',
                  fontWeight: 'bold'
                }} 
              />
            </Box>
          </Box>
          
          {loadingWallet ? (
            <Box sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Loading your financial data...
              </Typography>
              <LinearProgress />
            </Box>
          ) : walletInfo && !walletInfo.error ? (
            <>
              <Grid container sx={{ p: 3 }}>
                <Grid item xs={12} md={4} sx={{ p: 2, textAlign: 'center' }}>
                  <Box sx={{ 
                    mb: 1, 
                    p: 2, 
                    borderRadius: 2, 
                    bgcolor: 'rgba(255,255,255,0.7)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                  }}>
                    <Typography variant="overline" color="primary" sx={{ fontWeight: 500, letterSpacing: 1 }}>
                      AVAILABLE BALANCE
                    </Typography>
                    <Typography variant="h4" color="text.primary" sx={{ fontWeight: 'bold', mt: 1 }}>
                      {formatCurrency(walletInfo.balance)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Last updated: {formatDate(walletInfo.lastUpdated)}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={4} sx={{ p: 2, textAlign: 'center' }}>
                  <Box sx={{ 
                    mb: 1, 
                    p: 2, 
                    borderRadius: 2, 
                    bgcolor: 'rgba(255,255,255,0.7)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                  }}>
                    <Typography variant="overline" color="success.main" sx={{ fontWeight: 500, letterSpacing: 1 }}>
                      TOTAL RECEIVED
                    </Typography>
                    <Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold', mt: 1 }}>
                      {formatCurrency(walletTotals.totalReceived)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 0.5 }}>
                      <ArrowDownwardIcon color="success" fontSize="small" />
                      <Typography variant="caption" color="text.secondary">
                        All completed transactions
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={4} sx={{ p: 2, textAlign: 'center' }}>
                  <Box sx={{ 
                    mb: 1, 
                    p: 2, 
                    borderRadius: 2, 
                    bgcolor: 'rgba(255,255,255,0.7)', 
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                  }}>
                    <Typography variant="overline" color="primary" sx={{ fontWeight: 500, letterSpacing: 1 }}>
                      CURRENT DISBURSEMENT
                    </Typography>
                    <Typography variant="h4" color="primary.main" sx={{ fontWeight: 'bold', mt: 1 }}>
                      {formatCurrency(request.disbursedAmount || request.amount || 0)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 0.5 }}>
                      <PaymentsIcon color="primary" fontSize="small" sx={{ mr: 0.5 }} />
                      <Typography variant="caption" color="text.secondary">
                        {request.status === 'Disbursed' ? 'Pending confirmation' : 'Confirmed'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
              
              {/* Recent transactions section */}
              {walletTransactions.length > 0 && (
                <Box sx={{ px: 3 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    mt: 1,
                    mb: 2
                  }}>
                    <Typography variant="subtitle1" color="text.primary" sx={{ fontWeight: 'medium' }}>
                      <ReceiptLongIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Recent Transactions
                    </Typography>
                    <Button 
                      size="small" 
                      endIcon={<KeyboardArrowRightIcon />}
                      onClick={() => navigate('/member/wallet')}
                    >
                      View All
                    </Button>
                  </Box>
                  
                  <List sx={{ 
                    bgcolor: 'background.paper', 
                    borderRadius: 2,
                    mb: 3,
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                  }}>
                    {walletTransactions.slice(0, 3).map((transaction, index) => (
                      <ListItem 
                        key={transaction._id || index}
                        divider={index < Math.min(walletTransactions.length, 3) - 1}
                        secondaryAction={
                          <Typography 
                            variant="body2" 
                            color={transaction.type === 'credit' ? 'success.main' : 'error.main'}
                            sx={{ fontWeight: 'bold' }}
                          >
                            {transaction.type === 'credit' ? '+' : '-'}{formatCurrency(transaction.amount)}
                          </Typography>
                        }
                      >
                        <ListItemIcon>
                          <Avatar 
                            sx={{ 
                              bgcolor: transaction.type === 'credit' ? 'success.light' : 'error.light',
                              width: 32,
                              height: 32
                            }}
                          >
                            {transaction.type === 'credit' ? 
                              <ArrowDownwardIcon fontSize="small" /> : 
                              <ArrowUpwardIcon fontSize="small" />
                            }
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText 
                          primary={transaction.description}
                          secondary={formatDate(transaction.date)}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
              
              <Box sx={{ px: 3, pb: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Button 
                  variant="text" 
                  color="primary" 
                  startIcon={<AccountBalanceIcon />}
                  onClick={() => navigate('/member/wallet')}
                >
                  Wallet Dashboard
                </Button>
                
                {request.status === 'Disbursed' && (
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={() => setOpenConfirmReceiptDialog(true)}
                  >
                    Confirm Receipt
                  </Button>
                )}
              </Box>
            </>
          ) : (
            <Box sx={{ p: 3 }}>
              {walletInfo && walletInfo.error ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {walletInfo.message || "Wallet information couldn't be loaded"}
                </Alert>
              ) : (
                <Typography>Wallet information not available</Typography>
              )}
              
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Your disbursement details:
                </Typography>
                <Typography variant="h5" color="primary.main" sx={{ fontWeight: 'bold' }}>
                  {formatCurrency(request.disbursedAmount || request.amount || 0)}
                </Typography>
                
                {request.status === 'Disbursed' && (
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={() => setOpenConfirmReceiptDialog(true)}
                    sx={{ mt: 2 }}
                  >
                    Confirm Receipt
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Card>
      </Grid>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 3, px: { xs: 2, md: 3 } }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/member/financial-requests')}>
          Back to Requests
        </Button>
      </Box>
    );
  }

  if (!request) {
    return (
      <Box sx={{ py: 3, px: { xs: 2, md: 3 } }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          Request not found
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/member/financial-requests')}>
          Back to Requests
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3, px: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          Financial Assistance Request
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/member/financial-requests')}>
          Back to Requests
        </Button>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
          {steps.map((label, index) => (
            <Step key={label} completed={index < activeStep || request.status === 'Completed'}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardHeader title="Request Details" />
              <CardContent>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Status
                    </Typography>
                    <Chip 
                      label={statusDisplay[request.status] || request.status} 
                      color={getStatusColor(request.status)} 
                      sx={{ mt: 1 }}
                    />
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Reason
                    </Typography>
                    <Typography variant="body1">
                      {request.reason}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Amount Requested
                    </Typography>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                      <MonetizationOnIcon fontSize="small" sx={{ mr: 1 }} />
                      {request.amount > 0 ? `$${request.amount.toFixed(2)}` : 'Not specified'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Request Date
                    </Typography>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                      <AccessTimeIcon fontSize="small" sx={{ mr: 1 }} />
                      {formatDate(request.requestDate)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Last Updated
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(request.lastUpdated)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      SLA Status
                    </Typography>
                    <Box sx={{ 
                      mt: 1,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: (theme) => {
                        const slaStatus = getSlaStatus(request.status, request.lastUpdated || request.requestDate);
                        switch(slaStatus) {
                          case 'red': return 'error.light';
                          case 'amber': return 'warning.light';
                          case 'green': return 'success.light';
                          default: return 'divider';
                        }
                      }
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Expected Response Time
                        </Typography>
                        <Chip
                          size="small"
                          label={(() => {
                            const slaStatus = getSlaStatus(request.status, request.lastUpdated || request.requestDate);
                            switch(slaStatus) {
                              case 'green': return 'Within Target';
                              case 'amber': return 'Approaching Deadline';
                              case 'red': return 'Past Due';
                              default: return 'No Target';
                            }
                          })()}
                          sx={{
                            fontWeight: 'medium',
                            bgcolor: (theme) => {
                              const slaStatus = getSlaStatus(request.status, request.lastUpdated || request.requestDate);
                              switch(slaStatus) {
                                case 'red': return 'error.light';
                                case 'amber': return 'warning.light';
                                case 'green': return 'success.light';
                                default: return 'grey.200';
                              }
                            },
                            color: (theme) => {
                              const slaStatus = getSlaStatus(request.status, request.lastUpdated || request.requestDate);
                              switch(slaStatus) {
                                case 'red': return 'error.dark';
                                case 'amber': return 'warning.dark';
                                case 'green': return 'success.dark';
                                default: return 'text.primary';
                              }
                            },
                          }}
                        />
                      </Box>
                      
                      <Box sx={{ mt: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Box sx={{ flexGrow: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={(() => {
                                if (!request?.status || !request?.lastUpdated) return 0;
                                const slaLimit = SLA_CONFIG[request.status];
                                if (!slaLimit) return 0;
                                
                                const now = new Date();
                                const statusDate = new Date(request.lastUpdated || request.requestDate);
                                const elapsedHours = (now - statusDate) / (1000 * 60 * 60);
                                const percentComplete = Math.min(100, (elapsedHours / slaLimit) * 100);
                                return percentComplete;
                              })()}
                              sx={{
                                height: 8,
                                borderRadius: 4,
                                bgcolor: 'grey.200',
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: 4,
                                  bgcolor: (theme) => {
                                    const slaStatus = getSlaStatus(request.status, request.lastUpdated || request.requestDate);
                                    switch(slaStatus) {
                                      case 'red': return 'error.main';
                                      case 'amber': return 'warning.main';
                                      case 'green': return 'success.main';
                                      default: return 'primary.main';
                                    }
                                  }
                                }
                              }}
                            />
                          </Box>
                          <Typography variant="body2" sx={{ 
                            fontWeight: 'medium',
                            color: (theme) => {
                              const slaStatus = getSlaStatus(request.status, request.lastUpdated || request.requestDate);
                              switch(slaStatus) {
                                case 'red': return 'error.main';
                                case 'amber': return 'warning.main';
                                case 'green': return 'success.main';
                                default: return 'text.secondary';
                              }
                            }
                          }}>
                            {getSlaTimeRemaining(request.status, request.lastUpdated || request.requestDate)}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AccessTimeIcon fontSize="small" color="action" />
                        <Typography variant="caption" color="text.secondary">
                          Target: {SLA_CONFIG[request.status]} hours for {statusDisplay[request.status]} stage
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardHeader title="Actions Required" />
              <CardContent>
                {request.status === 'Approved' && (
                  <Box>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      Your request has been approved! Please accept or decline this approval.
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      <Button 
                        variant="contained" 
                        color="success" 
                        onClick={() => setOpenAcceptDialog(true)}
                      >
                        Accept
                      </Button>
                      <Button 
                        variant="outlined" 
                        color="error" 
                        onClick={() => setOpenRejectDialog(true)}
                      >
                        Decline
                      </Button>
                    </Stack>
                  </Box>
                )}

                {request.status === 'Disbursed' && (
                  <Box>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      The funds have been disbursed to you. Please confirm receipt once you have received them.
                    </Typography>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      onClick={() => setOpenConfirmReceiptDialog(true)}
                    >
                      Confirm Receipt
                    </Button>
                  </Box>
                )}

                {request.status === 'Declined' && (
                  <Typography variant="body1" color="error">
                    Your request has been declined. You may submit a new request if needed.
                  </Typography>
                )}

                {request.status === 'MemberRejected' && (
                  <Typography variant="body1">
                    You've declined this request. You may submit a new request if needed.
                  </Typography>
                )}

                {request.status === 'Completed' && (
                  <Typography variant="body1" color="success.main">
                    This request has been completed. Thank you!
                  </Typography>
                )}

                {(request.status === 'Requested' || request.status === 'Review') && (
                  <Typography variant="body1">
                    Your request is being processed. We'll notify you when there's an update.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Replace the existing wallet card with the enhanced version */}
          {renderEnhancedWallet()}
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Comments & Updates
        </Typography>

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Add a comment"
            placeholder="Type your comment here..."
            multiline
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={isSubmitting}
          />
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              disabled={!comment.trim() || isSubmitting}
              onClick={handleAddComment}
            >
              {isSubmitting ? <CircularProgress size={24} /> : 'Add Comment'}
            </Button>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box>
          {request.comments && request.comments.length > 0 ? (
            <Stack spacing={2}>
              {request.comments.map((comment, index) => (
                <Card key={index} variant="outlined" sx={{ 
                  bgcolor: comment.by === 'admin' ? 'grey.100' : 'primary.50'
                }}>
                  <CardContent>
                    <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {comment.by === 'admin' ? 'Admin' : 'You'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(comment.date)}
                      </Typography>
                    </Box>
                    <Typography variant="body2">{comment.text}</Typography>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary" align="center">
              No comments yet
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Accept Dialog */}
      <Dialog open={openAcceptDialog} onClose={() => setOpenAcceptDialog(false)}>
        <DialogTitle>Accept Approval</DialogTitle>
        <DialogContent>
          <DialogContentText>
            By accepting this approval, you confirm that you wish to receive the approved financial assistance.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAcceptDialog(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button 
            onClick={handleAcceptRequest} 
            color="primary" 
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={24} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={openRejectDialog} onClose={() => setOpenRejectDialog(false)}>
        <DialogTitle>Decline Approval</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please provide a reason for declining this approved request.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Reason for declining"
            fullWidth
            multiline
            rows={3}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            disabled={isSubmitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRejectDialog(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button 
            onClick={handleRejectRequest} 
            color="error" 
            variant="contained"
            disabled={isSubmitting || !rejectionReason.trim()}
          >
            {isSubmitting ? <CircularProgress size={24} /> : 'Decline'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Receipt Dialog */}
      <Dialog open={openConfirmReceiptDialog} onClose={() => setOpenConfirmReceiptDialog(false)}>
        <DialogTitle>Confirm Receipt</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please confirm that you have received the financial assistance funds that were disbursed to your digital wallet.
          </DialogContentText>
          {walletInfo && !walletInfo.error && (
            <Box sx={{ mt: 2, bgcolor: 'background.paper', p: 2, borderRadius: 1 }}>
              <Typography variant="subtitle2">Your Current Wallet Balance</Typography>
              <Typography variant="h6" color="success.main">
                {formatCurrency(walletInfo.balance || 0)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmReceiptDialog(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmReceipt} 
            color="primary" 
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={24} /> : 'Confirm Receipt'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MemberFinancialRequestDetailPage;